import {
  Box,
  Card,
  CardContent,
  CardHeader,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from "@mui/material";
import { useNavigate } from "react-router-dom";

import EditNoteIcon from "@mui/icons-material/Edit";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import FolderOpenIcon from "@mui/icons-material/FolderOpen";
import MapIcon from "@mui/icons-material/Map";
import SyncIcon from "@mui/icons-material/Sync";

import { useMemo, useState } from "react";
import useCheckPermission from "../../../../common/hooks/useCheckPermission";
import useGetPageInfo from "../../../../common/hooks/useGetPageInfo";
import BreadcrumbsComponent from "../../../../components/shared/breadcrumbs";
import { buildCheckNewRequestModifyPath } from "../../shared/checkNewRequest/checkNewRequestPageUtils";
import {
  buildProjectWorkspacePath,
  WORKSPACE_LIST_VIEWS,
} from "../../shared/projectWorkspace/projectWorkspacePaths";
import RequestFollowUpDialog from "../../../../components/request/RequestFollowUpDialog";
import EnhancedTable from "../../../../components/shared/table";
import RequestMapDialog from "./RequestMapDialog";
import { assignmentOwnerColumn } from "../../../../components/request/requestAssignmentDisplay";
import { useAssignmentFilteredList } from "../../../../components/request/useAssignmentFilteredList";

const InProcessRequestPage = ({ hubMode = false } = {}) => {
  const pageInfo = useGetPageInfo();
  const {
    rows: dataList,
    assignmentFilter,
    setAssignmentFilter,
    filterOptions: assignmentFilterOptions,
    showAssignmentUi,
  } = useAssignmentFilteredList("SLN/Management/InProcessRequest", true);
  let navigate = useNavigate();
  const [selectedPowerPlantType, setSelectedPowerPlantType] = useState("");
  const [selectedLocationUsage, setSelectedLocationUsage] = useState("");
  const [selectedLeadPriority, setSelectedLeadPriority] = useState("");
  const [selectedFollowUpStatus, setSelectedFollowUpStatus] = useState("");
  const [minArea, setMinArea] = useState("");
  const [maxArea, setMaxArea] = useState("");
  const [followUpRequestId, setFollowUpRequestId] = useState(null);

  const [selectedRequest, setSelectedRequest] = useState({
    open: false,
    id: null,
  });
  const headCells = [
    {
      id: "updateOn",
      label: "تاریخ",
      type: "date",
    },
    {
      id: "leadPriorityTitle",
      label: "اولویت پیگیری",
    },
    {
      id: "leadScore",
      label: "امتیاز",
    },
    {
      id: "followUpCount",
      label: "تعداد پیگیری",
    },
    {
      id: "lastFollowUpDate",
      label: "آخرین پیگیری",
      type: "date",
    },
    {
      id: "nextFollowUpDate",
      label: "پیگیری بعدی",
      type: "date",
    },
    {
      id: "followUpReminderTitle",
      label: "یادآور پیگیری",
      type: "chip",
      model: { colorField: "followUpReminderColor" },
    },
    ...(showAssignmentUi ? [assignmentOwnerColumn] : []),

    {
      id: "projectOwnerFirstName",
      label: "نام",
    },
    {
      id: "projectOwnerLastName",
      label: "نام خانوادگی",
    },

    {
      id: "projectTitle",
      label: "عنوان پروژه",
    },
    {
      id: "referenceID",
      label: "شناسه درخواست",
    },
    {
      id: "powerPlantTypeTitle",
      label: "نوع نیروگاه",
    },
    {
      id: "locationUsageTypeTitle",
      label: "کاربری محل",
    },
    {
      id: "installableAreaSquareMeterDisplay",
      label: "مساحت قابل نصب",
    },
    {
      id: "expireDate",
      label: "تاریخ اعتبار",
      type: "date",
    },
    {
      id: "adminComment",
      label: "یادداشت مدیر",
    },
    {
      id: "agreementKind",
      label: "نوع توافق",
    },
    {
      id: "projectKind",
      label: "نوع درخواست",
    },
    {
      id: "requestConfirm",
      label: "وضعیت درخواست",
    },
    {
      id: "cityName",
      label: "شهرستان",
    },
    {
      id: "substationName",
      label: "ایستگاه",
    },
    {
      id: "feederName",
      label: "فیدر",
    },
    {
      id: "stepTitle",
      label: "مرحله فعلی",
    },
  ];

  const onEditClicked = (row) => {
    const path = buildCheckNewRequestModifyPath(row, "management");
    if (path) navigate(path);
  };

  const onOpenWorkspace = (row) => {
    const path = buildProjectWorkspacePath(row, "management", {
      listView: WORKSPACE_LIST_VIEWS.inProcess,
    });
    if (path) navigate(path);
  };

  const onFetchData = () => {
    navigate();
  };

  const onOpenRequestMap = (row) => {
    setSelectedRequest({
      open: true,
      id: row.id,
    });
  };

  const rows = (Array.isArray(dataList) ? dataList : []).map((row) => ({
    ...row,
    installableAreaSquareMeterDisplay:
      row.installableAreaSquareMeter != null
        ? row.installableAreaSquareMeter
        : "ثبت نشده",
  }));
  const powerPlantTypeOptions = useMemo(
    () =>
      [...new Set(rows.map((x) => x.powerPlantTypeTitle).filter(Boolean))].sort(),
    [rows]
  );
  const locationUsageOptions = useMemo(
    () =>
      [...new Set(rows.map((x) => x.locationUsageTypeTitle).filter(Boolean))].sort(),
    [rows]
  );
  const filteredRows = useMemo(() => {
    return rows.filter((row) => {
      const area = Number(row.installableAreaSquareMeter);
      const passesType =
        !selectedPowerPlantType || row.powerPlantTypeTitle === selectedPowerPlantType;
      const passesUsage =
        !selectedLocationUsage || row.locationUsageTypeTitle === selectedLocationUsage;
      const passesLeadPriority =
        !selectedLeadPriority || row.leadPriorityTitle === selectedLeadPriority;
      const hasNoFollowUp = Number(row.followUpCount || 0) === 0;
      const passesFollowUpStatus =
        !selectedFollowUpStatus ||
        (selectedFollowUpStatus === "overdue" &&
          row.followUpReminderStatus === "overdue") ||
        (selectedFollowUpStatus === "today" &&
          row.followUpReminderStatus === "today") ||
        (selectedFollowUpStatus === "none" && hasNoFollowUp);
      const passesMinArea = !minArea || (!Number.isNaN(area) && area >= Number(minArea));
      const passesMaxArea = !maxArea || (!Number.isNaN(area) && area <= Number(maxArea));
      return (
        passesType &&
        passesUsage &&
        passesLeadPriority &&
        passesFollowUpStatus &&
        passesMinArea &&
        passesMaxArea
      );
    });
  }, [
    rows,
    selectedPowerPlantType,
    selectedLocationUsage,
    selectedLeadPriority,
    selectedFollowUpStatus,
    minArea,
    maxArea,
  ]);

  return (
    <Grid container spacing={3}>
      <Grid item xs={12} md={12}>
        {!hubMode ? <BreadcrumbsComponent /> : null}
      </Grid>

      <RequestMapDialog
        open={selectedRequest.open}
        onClose={() => {
          setSelectedRequest({
            open: false,
            id: null,
          });
        }}
        id={selectedRequest.id}
      />

      <Grid item xs={12} md={12}>
        <Card>
          <CardHeader
            title={pageInfo.title}
            action={
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  width: "fit-content",
                }}
              >
                <IconButton
                  variant="text"
                  color="primary"
                  onClick={onFetchData}
                >
                  <SyncIcon />
                </IconButton>
              </Box>
            }
          />

          <CardContent>
            <Grid container spacing={2} sx={{ mb: 2 }}>
              {showAssignmentUi && (
                <Grid item xs={12} md={3}>
                  <FormControl fullWidth size="small">
                    <InputLabel>وضعیت مسئولیت</InputLabel>
                    <Select
                      value={assignmentFilter}
                      label="وضعیت مسئولیت"
                      onChange={(event) =>
                        setAssignmentFilter(event.target.value)
                      }
                    >
                      {assignmentFilterOptions.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              )}
              <Grid item xs={12} md={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>نوع نیروگاه</InputLabel>
                  <Select
                    value={selectedPowerPlantType}
                    label="نوع نیروگاه"
                    onChange={(event) => setSelectedPowerPlantType(event.target.value)}
                  >
                    <MenuItem value="">همه</MenuItem>
                    {powerPlantTypeOptions.map((option) => (
                      <MenuItem key={option} value={option}>
                        {option}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>کاربری محل</InputLabel>
                  <Select
                    value={selectedLocationUsage}
                    label="کاربری محل"
                    onChange={(event) => setSelectedLocationUsage(event.target.value)}
                  >
                    <MenuItem value="">همه</MenuItem>
                    {locationUsageOptions.map((option) => (
                      <MenuItem key={option} value={option}>
                        {option}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={2}>
                <TextField
                  fullWidth
                  size="small"
                  label="حداقل مساحت"
                  type="number"
                  value={minArea}
                  onChange={(event) => setMinArea(event.target.value)}
                />
              </Grid>
              <Grid item xs={12} md={2}>
                <TextField
                  fullWidth
                  size="small"
                  label="حداکثر مساحت"
                  type="number"
                  value={maxArea}
                  onChange={(event) => setMaxArea(event.target.value)}
                />
              </Grid>
              <Grid item xs={12} md={2}>
                <FormControl fullWidth size="small">
                  <InputLabel>اولویت پیگیری</InputLabel>
                  <Select
                    value={selectedLeadPriority}
                    label="اولویت پیگیری"
                    onChange={(event) => setSelectedLeadPriority(event.target.value)}
                  >
                    <MenuItem value="">همه</MenuItem>
                    <MenuItem value="اولویت خیلی بالا">اولویت خیلی بالا</MenuItem>
                    <MenuItem value="اولویت بالا">اولویت بالا</MenuItem>
                    <MenuItem value="اولویت متوسط">اولویت متوسط</MenuItem>
                    <MenuItem value="نیاز به بررسی">نیاز به بررسی</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>وضعیت پیگیری</InputLabel>
                  <Select
                    value={selectedFollowUpStatus}
                    label="وضعیت پیگیری"
                    onChange={(event) => setSelectedFollowUpStatus(event.target.value)}
                  >
                    <MenuItem value="">همه</MenuItem>
                    <MenuItem value="overdue">پیگیری عقب افتاده</MenuItem>
                    <MenuItem value="today">پیگیری امروز</MenuItem>
                    <MenuItem value="none">بدون پیگیری</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
            <Grid container>
              <Grid item xs={12} md={12}>
                <EnhancedTable
                  rowKey={"id"}
                  rows={filteredRows}
                  headCells={headCells}
                  getRowStatus={(row) =>
                    row.followUpReminderStatus === "overdue" ? "error" : ""
                  }
                  defaultSort={{ orderBy: "updateOn", order: "desc" }}
                  pageSize={100}
                  actions={[
                    {
                      icon: useCheckPermission("edit") ? <MapIcon /> : null,
                      onClick: onOpenRequestMap,
                      color: "info",
                      tooltip: "نقشه",
                    },
                    {
                      icon: useCheckPermission("edit") ? (
                        <FolderOpenIcon fontSize="small" />
                      ) : null,
                      onClick: onOpenWorkspace,
                      color: "primary",
                      tooltip: "پرونده پروژه (Workspace)",
                    },
                    {
                      icon: useCheckPermission("edit") ? (
                        <EditOutlinedIcon fontSize="small" />
                      ) : null,
                      onClick: onEditClicked,
                      color: "secondary",
                      tooltip: "ویرایش درخواست",
                    },
                    {
                      icon: useCheckPermission("edit") ? <EditNoteIcon /> : null,
                      onClick: (row) => setFollowUpRequestId(row.id),
                      color: "secondary",
                      tooltip: "ثبت پیگیری",
                    },
                  ]}
                />
              </Grid>
            </Grid>
            <RequestFollowUpDialog
              open={!!followUpRequestId}
              onClose={() => setFollowUpRequestId(null)}
              source="Management"
              requestInfoId={followUpRequestId}
              onSubmitted={onFetchData}
            />
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );
};

export default InProcessRequestPage;
